import type { Commodity } from '@enmon/commons/lib/shared';
import type {
  LocationBootstrapManufacturer,
  LocationBootstrapMeterModel,
} from '@/features/location-bootstrap/location-bootstrap-types';

interface StrapiEntity {
  id?: number;
  name?: string;
  [key: string]: unknown;
}

export function readMeterModels(response: { data?: unknown[] }): LocationBootstrapMeterModel[] {
  return (response.data ?? [])
    .map(readMeterModel)
    .filter((model): model is LocationBootstrapMeterModel => Boolean(model))
    .toSorted((left, right) => left.name.localeCompare(right.name));
}

export function deriveManufacturers(models: LocationBootstrapMeterModel[]) {
  const manufacturers = new Map<string, LocationBootstrapManufacturer>();

  for (const model of models) {
    if (model.manufacturer?.name) {
      manufacturers.set(String(model.manufacturer.id), model.manufacturer);
    }
  }

  return [...manufacturers.values()].toSorted((left, right) => left.name.localeCompare(right.name));
}

function readMeterModel(value: unknown): LocationBootstrapMeterModel | null {
  const entity = readEntity(value);

  if (!entity) {
    return null;
  }

  const id = entity.id;
  const name = entity.name;

  if (!(id && name)) {
    return null;
  }

  return {
    details: entity.details as LocationBootstrapMeterModel['details'],
    id,
    manufacturer: readManufacturer(entity.manufacturer),
    commodity: entity.commodity as Commodity,
    name,
    raw: entity,
  };
}

function readManufacturer(value: unknown): LocationBootstrapManufacturer | undefined {
  const entity = readEntity(value);

  if (!entity) {
    return undefined;
  }

  const id = entity.id;
  const name = entity.name;

  if (!(id && name)) return undefined;

  const parentId = readEntity(entity.parent)?.id;

  return { id, name, parent: parentId ? { id: parentId } : null };
}

function readEntity(value: unknown): StrapiEntity | null {
  const object = readObject(value);
  const data = readObject(object?.data);

  return (data ?? object) as StrapiEntity | null;
}

function readObject(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}
